import { useEffect, useRef, useState } from 'react'
import { US_STATES } from '../../utils/constants.js'
import {
  jurisdictionStack,
  lookupZip,
  saveJurisdiction,
  clearJurisdiction,
} from '../../utils/jurisdiction.js'
import { track } from '../../utils/analytics.js'

export function JurisdictionPane({ jurisdiction, onChange }) {
  const [country] = useState('US')
  const [stateCode, setStateCode] = useState(jurisdiction?.state || '')
  const [city, setCity] = useState(jurisdiction?.city || '')
  const [zip, setZip] = useState(jurisdiction?.zip || '')
  const [district, setDistrict] = useState(jurisdiction?.district || '')
  const [looking, setLooking] = useState(false)
  const [message, setMessage] = useState(null)
  const [justSaved, setJustSaved] = useState(false)

  // Don't fire the lookup on first render — only when the user actually
  // edits the ZIP. Otherwise we'd overwrite manually-edited fields on every
  // page load.
  const initialZip = useRef(jurisdiction?.zip || '')
  const dirty = useRef(false)

  useEffect(() => {
    if (!dirty.current) {
      if (zip !== initialZip.current) dirty.current = true
      else return
    }
    if (!/^\d{5}$/.test(zip)) {
      setLooking(false)
      return
    }

    const controller = new AbortController()
    setLooking(true)
    setMessage(null)

    ;(async () => {
      try {
        const result = await lookupZip(zip, { signal: controller.signal })
        if (controller.signal.aborted) return
        if (!result) {
          setMessage({
            type: 'warn',
            text: 'No match for that ZIP. Enter the other fields manually.',
          })
          return
        }
        if (result.state)    setStateCode(result.state)
        if (result.city)     setCity(result.city)
        if (result.district) setDistrict(result.district)
        if (result.warn) {
          setMessage({ type: 'warn', text: result.warn })
        } else if (result.district) {
          setMessage({
            type: 'ok',
            text: `Located ${result.city}, ${result.state} — district ${result.district}.`,
          })
        } else if (result.city && result.state) {
          setMessage({
            type: 'ok',
            text: `Located ${result.city}, ${result.state}.`,
          })
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('[Verdant] ZIP lookup threw', err)
          setMessage({
            type: 'warn',
            text: `ZIP lookup failed: ${err?.message || 'unknown error'}.`,
          })
        }
      } finally {
        if (!controller.signal.aborted) setLooking(false)
      }
    })()

    return () => controller.abort()
  }, [zip])

  const draft = { country, state: stateCode, city, zip, district }
  const stack = jurisdictionStack(draft)

  const handleSave = (e) => {
    e.preventDefault()
    const next = {
      country: 'US',
      state: stateCode || null,
      city: city || null,
      zip: zip || null,
      district: district || null,
    }
    saveJurisdiction(next)
    onChange?.(next)
    track('jurisdiction_saved', {
      level: next.city ? 'city' : next.state ? 'state' : 'federal',
    })
    initialZip.current = next.zip || ''
    dirty.current = false
    setMessage({ type: 'ok', text: 'Jurisdiction saved. The header pill now reflects this location.' })
    setJustSaved(true)
    setTimeout(() => setJustSaved(false), 2000)
  }

  const handleClear = () => {
    clearJurisdiction()
    setStateCode('')
    setCity('')
    setZip('')
    setDistrict('')
    onChange?.(null)
    initialZip.current = ''
    dirty.current = false
    setMessage({ type: 'ok', text: 'Jurisdiction cleared.' })
  }

  return (
    <section className="pane-card">
      <h2>Your jurisdiction</h2>
      <p className="card-summary" style={{ marginTop: 4, marginBottom: 16 }}>
        Stored locally in your browser. We keep jurisdiction codes only — never
        your full address. Enter your ZIP code and the rest will fill in.
      </p>

      <form onSubmit={handleSave}>
        <div className="field-row">
          <div className="field">
            <label>Country</label>
            <select value={country} disabled>
              <option value="US">United States</option>
            </select>
          </div>
          <div className="field">
            <label>
              ZIP code
              {looking && <span className="form-hint" style={{ marginLeft: 8 }}>Looking up…</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="98101"
              autoComplete="postal-code"
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label>State</label>
            <select value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
              <option value="">Select state…</option>
              {US_STATES.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Seattle"
            />
          </div>
        </div>

        <div className="field">
          <label>Congressional district</label>
          <input
            type="text"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            placeholder="e.g. 7"
          />
        </div>

        {stack.length > 0 && (
          <div className="field">
            <label>Jurisdiction stack</label>
            <div className="stack-display">
              {stack.join(' › ')}
            </div>
          </div>
        )}

        {message && (
          <div
            className={message.type === 'warn' ? 'error-state' : 'note'}
            style={{ marginBottom: 12 }}
            role="status"
            aria-live="polite"
          >
            {message.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn-primary">
            {justSaved ? 'Saved ✓' : 'Save jurisdiction'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={handleClear}>
            Clear
          </button>
        </div>

        <div className="note">
          ZIP → place lookup is courtesy of zippopotam.us; the congressional
          district comes from the public US Census Geocoder. Neither call
          stores your address — only the resolved jurisdiction codes (state,
          city, district) are saved.
        </div>
      </form>
    </section>
  )
}
